import { UNDERLINE_STATIC } from './link-underline';
import type { ReactNode } from 'react';
import { CloseIcon } from '@icons/CloseIcon';
import { cn } from '@/lib/utils';

interface FileIconBoxProps {
  /** Size the box via className (e.g. `size-12`); the rounding, fill and centering are fixed. */
  className?: string;
  children: ReactNode;
}

/** Rounded container behind a file-type glyph, shared by every file chip. */
export function FileIconBox({ className, children }: FileIconBoxProps) {
  return (
    <div className={cn('flex shrink-0 items-center justify-center rounded-xs bg-dinnerplate-white', className)}>
      {children}
    </div>
  );
}

interface FileAttachmentProps {
  /** Leading visual — a FileIconBox, thumbnail image or placeholder. The caller controls its size. */
  leading?: ReactNode;
  /** File name / title. Truncates with a trailing ellipsis when it overflows. */
  name: string;
  /** When set, the name links to the file (new tab); otherwise it's plain text. */
  href?: string | null;
  /** Node rendered inline right after the name (e.g. an expand caret). */
  nameAdornment?: ReactNode;
  /** Secondary line under the name (e.g. a timestamp). */
  subtitle?: ReactNode;
  /** Far-right content (e.g. actions or a status icon). Takes precedence over `onRemove`. */
  trailing?: ReactNode;
  /** Convenience remove control rendered at the far right when `trailing` is unset. */
  onRemove?: () => void;
  removeLabel?: string;
  className?: string;
}

/** A file chip: a leading glyph/thumbnail, the file name (link or text) and optional subtitle/actions. */
export function FileAttachment({
  leading,
  name,
  href,
  nameAdornment,
  subtitle,
  trailing,
  onRemove,
  removeLabel,
  className,
}: FileAttachmentProps) {
  return (
    <div className={cn('flex min-w-0 items-center gap-3', className)}>
      {leading}
      <div className="flex min-w-0 flex-col gap-1">
        <div className="flex min-w-0 items-center gap-2">
          {href ? (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className={cn('min-w-0 truncate rounded-xs font-inter text-sm font-semibold text-off-black focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-off-black', UNDERLINE_STATIC)}
            >
              {name}
            </a>
          ) : (
            <span className="min-w-0 truncate font-inter text-sm font-semibold text-off-black">{name}</span>
          )}
          {nameAdornment}
        </div>
        {subtitle}
      </div>
      {trailing ??
        (onRemove && (
          <button
            type="button"
            aria-label={removeLabel}
            onClick={onRemove}
            className="ml-auto shrink-0 rounded-xs text-grey focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-off-black"
          >
            <CloseIcon className="size-4" />
          </button>
        ))}
    </div>
  );
}
